import { Component, OnInit } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { Router } from "@angular/router";
import { showAlert } from "src/app/utils/alert";
import { AuthService } from "../../services/auth.service";
import { TermsBottomSheetComponent } from "../terms-bottom-sheet/terms-bottom-sheet.component";

@Component({
	selector: 'app-change-password',
	templateUrl: './change-password.component.html',
	styleUrls: ['./change-password.component.scss'],
})
export class ChangePasswordComponent implements OnInit {
	token: string | null = null;
	loading: boolean = true;
	showPassword: boolean = false;
	showConfirmPassword: boolean = false;
	openedTerms: boolean = false;

	private minLengthValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
		return control.value && control.value.length >= 8 ? null : { minLength: true };
	};

	private numberValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
		return /\d/.test(control.value) ? null : { noNumber: true };
	};

	private uppercaseValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
		return /[A-Z]/.test(control.value) ? null : { noUppercase: true };
	};

	private passwordsMatchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
		const password = control.parent?.get('password')?.value;
		const confirmPassword = control.value;

		if (!password || !confirmPassword) {
			return null;
		}

		return password === confirmPassword ? null : { passwordsMismatch: true };
	};

	form: FormGroup = new FormGroup({
		password: this._formBuilder.control('', [
			Validators.required,
			this.minLengthValidator,
			this.numberValidator,
			this.uppercaseValidator
		]),
		confirmPassword: this._formBuilder.control('', [Validators.required, this.passwordsMatchValidator]),
		acceptedTerms: this._formBuilder.control(false, [Validators.requiredTrue])
	});

	get passwordField() {
		return this.form.controls['password'];
	}

	get confirmPasswordField() {
		return this.form.controls['confirmPassword'];
	}

	get acceptedTermsField() {
		return this.form.controls['acceptedTerms'];
	}

	get passwordMismatchError(){
		return this.confirmPasswordField.touched && this.confirmPasswordField.hasError('passwordsMismatch');
	}

	constructor(
		private _router: Router,
		private _formBuilder: FormBuilder,
		private _authService: AuthService,
		private _bottomSheet: MatBottomSheet
	) {
		const queryParams = this._router.getCurrentNavigation()?.finalUrl?.queryParams;
		this.token = queryParams?.['t'] || null;
	}

	ngOnInit(): void {
		this.passwordField.valueChanges.subscribe(() => {
			this.confirmPasswordField.updateValueAndValidity({ onlySelf: true });
		});

		this._authService.checkPasswordToken(this.token || '').subscribe({
			next: (response) => {
				this.loading = false;
				if (!response.valid || response.used) {
					this.token = null;
				}
			},
			error: (_) => {
				this.loading = false;
				this.token = null;
			}
		});
	}

	navigateToLogin() {
		this._router.navigate(['/auth/login']);
	}

	togglePassword(event: MouseEvent) {
		this.showPassword = !this.showPassword;
		event.stopPropagation();
	}

	toggleConfirmPassword(event: MouseEvent) {
		this.showConfirmPassword = !this.showConfirmPassword;
		event.stopPropagation();
	}

	openTerms(event: Event) {
		event.preventDefault();
		this.openedTerms = true;
		this._bottomSheet.open(TermsBottomSheetComponent);
	}

	private showErrorAlert(){
		showAlert('Se presentó un inconveniente al restablecer la contraseña. Si el problema persiste, comuníquese con el administrador del sistema.', undefined, 'error');
	}

	onSubmit() {
		if (this.form.valid) {
			this.loading = true;
			const { password } = this.form.value;
			this._authService.changePassword(this.token!, password).subscribe({
				next: (response) => {
					this.loading = false;
					if (response.success) {
						showAlert('Su contraseña ha sido restablecida correctamente.', () => {
							this._router.navigate(['/auth/login']);
						}, 'success');
					} else {
						this.showErrorAlert();
					}
				},
				error: () => {
					this.loading = false;
					this.showErrorAlert();
				}
			});
		}
	}
}
